/**
 * Text utilities for canvas text elements
 * Extracted from Canvas.jsx to reduce file size
 */
import Konva from "konva";

/**
 * Strips HTML tags from a string and normalizes whitespace.
 * @param {string} html - The HTML string to strip.
 * @returns {string} The plain text content.
 */
export const stripHtml = (html) => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  return tempDiv.innerText.replace(/\s+/g, " ").trim();
};

/**
 * Helper function to strip HTML but preserve formatting (newlines from <br>, <p>).
 * @param {string} html - The HTML string.
 * @returns {string} The plain text with preserved formatting.
 */
export const stripHtmlPreserveFormatting = (html) => {
  if (!html) return "";

  let text = html.replace(/<br\s*\/?>/gi, "\n");
  text = text.replace(/<\/p><p>/gi, "\n\n");
  text = text.replace(/<p>/gi, "");
  text = text.replace(/<\/p>/gi, "\n");

  text = text.replace(/<[^>]*>/g, ""); // Remove all other HTML tags
  text = text.replace(/&nbsp;/g, " ");
  text = text.replace(/&amp;/g, "&");
  text = text.replace(/&lt;/g, "<");
  text = text.replace(/&gt;/g, ">");

  return text.trim();
};

/**
 * Helper function to check if HTML content is effectively empty.
 * @param {string} htmlContent - The HTML string.
 * @returns {boolean} True if content is empty, false otherwise.
 */
export const isContentEmpty = (htmlContent) => {
  if (!htmlContent) return true;
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlContent;
  const textContent = tempDiv.textContent || tempDiv.innerText || "";
  return !textContent.trim();
};

/**
 * Helper functions to check for content presence
 */
export const hasUserContent = (text) => {
  return text.content && !isContentEmpty(text.content);
};

export const hasTaskContent = (text) => {
  return text.task && !isContentEmpty(text.task);
};

export const hasNoteContent = (text) => {
  return text.note && !isContentEmpty(text.note);
};

/**
 * Formats list content with bullets or numbers.
 * @param {string} content - The content string.
 * @param {string} type - 'bullet' or 'number'.
 * @returns {string} Formatted content.
 */
export const formatListContent = (content, type) => {
  if (!type || !content) return content;

  const lines = content.split("\n");
  return lines
    .map((line, index) => {
      if (line.trim() === "") return ""; // Handle empty lines
      if (type === "bullet") return `• ${line}`;
      if (type === "number") return `${index + 1}. ${line}`;
      return line;
    })
    .filter(Boolean) // Remove empty strings
    .join("\n");
};

/**
 * Calculates the appropriate width for a text block based on its content.
 * @param {object} text - The text object.
 * @param {Function} getTextStyle - Function to get text style for a section
 * @returns {number} The calculated width.
 */
export const calculateTextBlockWidth = (text, getTextStyle) => {
  // If a specific width is provided, use it
  if (text.width) {
    return text.width;
  }

  let maxContentWidth = 0;
  const minWidth = 250; // Minimum width for overall readability
  const padding = 20; // Internal padding within the text box

  // Helper to measure text width using Konva.Text for accuracy
  const measureKonvaTextWidth = (content, fontSize, fontFamily) => {
    if (!content) return 0;
    const dummyText = new Konva.Text({
      text: content,
      fontSize: fontSize,
      fontFamily: fontFamily,
      lineHeight: 1.2, // Match actual Konva line height
    });
    return dummyText.width();
  };

  // Calculate width for Parts section
  if (text.partsByType) {
    for (const [typeName, typeData] of Object.entries(text.partsByType)) {
      if (!typeData.parts || typeData.parts.length === 0) continue;

      const typeFontSize = text.textStyles?.type?.fontSize || 16;
      const typeFontFamily = text.textStyles?.type?.fontFamily || "Roboto";
      maxContentWidth = Math.max(
        maxContentWidth,
        measureKonvaTextWidth(typeName, typeFontSize, typeFontFamily)
      );

      if (typeData.parts && typeData.parts.length > 0) {
        const partFontSize = text.textStyles?.part?.fontSize || 14;
        const partFontFamily = text.textStyles?.part?.fontFamily || "Roboto";

        typeData.parts.forEach((partData) => {
          // Skip null or undefined partData entries
          if (!partData) return;
          
          const { part, qty, unit } = partData;
          const line = `${part || ""}${
            qty && parseInt(qty) > 0
              ? ` - Qty-${qty} ${unit || ""}`
              : ` ${unit || ""}`
          }`;
          maxContentWidth = Math.max(
            maxContentWidth,
            measureKonvaTextWidth(line, partFontSize, partFontFamily)
          );
        });
      }
    }
  }

  // Calculate width for Task content
  if (hasTaskContent(text)) {
    const taskContent = stripHtmlPreserveFormatting(text.task);
    const taskFontSize = getTextStyle(text, "task").fontSize || 14;
    const taskFontFamily = getTextStyle(text, "task").fontFamily || "Roboto";
    // For multiline HTML content, split by newline and measure the longest line.
    taskContent.split("\n").forEach((line) => {
      maxContentWidth = Math.max(
        maxContentWidth,
        measureKonvaTextWidth(line, taskFontSize, taskFontFamily)
      );
    });
  }

  // Calculate width for Note content
  if (hasNoteContent(text)) {
    const noteContent = stripHtmlPreserveFormatting(text.note);
    const noteFontSize = getTextStyle(text, "note").fontSize || 14;
    const noteFontFamily = getTextStyle(text, "note").fontFamily || "Roboto";
    noteContent.split("\n").forEach((line) => {
      maxContentWidth = Math.max(
        maxContentWidth,
        measureKonvaTextWidth(line, noteFontSize, noteFontFamily)
      );
    });
  }

  // Calculate width for User Content
  if (hasUserContent(text)) {
    const userContent = stripHtmlPreserveFormatting(text.content);
    const contentFontSize = getTextStyle(text, "content").fontSize || 14;
    const contentFontFamily =
      getTextStyle(text, "content").fontFamily || "Roboto";
    userContent.split("\n").forEach((line) => {
      maxContentWidth = Math.max(
        maxContentWidth,
        measureKonvaTextWidth(line, contentFontSize, contentFontFamily)
      );
    });
  }

  // Add padding to the maximum calculated content width.
  return Math.max(minWidth, maxContentWidth + padding * 2);
};
